import { Alert, Box, Checkbox, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import { showUserDisplayName } from '../../../../lib/userUtils';
import { tokens } from '../../../../mui-theme';
import { useProjectUsers } from '../../../../query/apiQueries';
import { formatCountryOptions } from '../../../../static-data/formatCountryOptions';
import ActionButton from '../../../../ui/primitives/ActionButton';
import CustomInputField from '../../../../ui/primitives/CustomInputField';
import CustomSelect from '../../../../ui/primitives/CustomSelect';
import SearchBar from '../../../../ui/primitives/SearchBar';
import CustomAvatar from '../../../elements/CustomAvatar';
import TestCaseTable from './TestCaseTable';

const NewIterationForm = ({
    environments,
    loading,
    selectedEnvironment,
    setSelectedEnvironment,
    selectedTestCases,
    setSelectedTestCases,
    iterationName,
    setIterationName,
    testCycleCountries,
    selectedCountry,
    setSelectedCountry,
    mode,
    testCycleId,
    executions = [],
    fetchTestCases,
    noTestCasesInCycle = false,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [tempSelectedIds, setTempSelectedIds] = useState([]);
    const { projectId } = useParams();
    const { data: users = [] } = useProjectUsers(projectId);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkTester, setBulkTester] = useState('');
    const [modalAssociations, setModalAssociations] = useState([]);
    const { addNotification } = useNotification();

    useEffect(() => {
        if (mode === 'edit' && executions.length > 0) {
            const mapped = executions.map(ex => ({
                testCaseAssociationId: ex.association.id,
                testCaseId: ex.test_case_id,
                testCaseKey: ex.association.testcase.key,
                summary: ex.association.testcase.summary,
                assignedTo: ex.executed_by ?? null,
                testSuites: ex.association.testcase?.test_suites || [],
                isEditable: ex.is_editable ?? false,
            }));
            setSelectedTestCases(mapped);
            setTempSelectedIds(mapped.map(x => x.testCaseAssociationId));
        }
    }, [mode, executions]);

    const filteredSelected = useMemo(() => {
        if (!searchValue.trim()) return selectedTestCases;
        const q = searchValue.toLowerCase();
        return selectedTestCases.filter(tc =>
            tc.testCaseKey?.toLowerCase().includes(q) ||
            tc.summary?.toLowerCase().includes(q)
        );
    }, [searchValue, selectedTestCases]);

    const hasNonEditableExecutions = useMemo(
        () => selectedTestCases.some(tc => tc.isEditable === false),
        [selectedTestCases]
    );

    const applySelection = () => {
        if (mode === 'edit') return;

        const finalList = tempSelectedIds
            .map(id => {
                const found = modalAssociations.find(x => x.id === id);
                if (!found) return null;
                return {
                    testCaseAssociationId: found.id,
                    testCaseId: found.testcase.id,
                    testCaseKey: found.testcase.key,
                    summary: found.testcase.summary,
                    assignedTo: found.assigned_to ?? null,
                    testSuites: found.testcase?.test_suites || [],
                };
            })
            .filter(Boolean);

        setSelectedTestCases(finalList);
        setShowModal(false);
    };

    const assignTesterBulk = () => {
        if (!bulkTester || selectedRows.length === 0) return;

        const updated = selectedTestCases.map(tc => {
            if (selectedRows.includes(tc.testCaseAssociationId)) {
                return { ...tc, assignedTo: bulkTester, update: tc.assignedTo !== bulkTester };
            }
            return tc;
        });

        setSelectedTestCases(updated);
        setSelectedRows([]);
        setBulkTester('');
    };

    const toggleRow = id => {
        setSelectedRows(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const editableTestCaseIds = useMemo(
        () => selectedTestCases
            .filter(tc => tc.isEditable !== false)
            .map(tc => tc.testCaseAssociationId),
        [selectedTestCases]
    );

    const isAllSelected =
        editableTestCaseIds.length > 0 &&
        editableTestCaseIds.every(id => selectedRows.includes(id));

    const countryOptions = formatCountryOptions(testCycleCountries);
    const shouldShowCountry =
        mode !== 'edit'
            ? countryOptions.length > 0
            : selectedCountry && countryOptions.some(item => item.value === selectedCountry);

    const environmentOptions = environments.map(env => ({ id: env.id, name: env.name }));

    const countrySelectOptions = countryOptions.map(item => {
        const flag = item.display.props.children[0];
        const code = flag.props.countryCode;
        const flagEmoji = code
            .toUpperCase()
            .replace(/./g, char => String.fromCodePoint(0x1f1e6 + char.charCodeAt() - 65));
        return { id: item.value, name: `${flagEmoji} ${item.value}` };
    });

    const userOptions = users.map(u => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name}`,
    }));

    return (
        <>
            <Stack spacing={2.5}>
                {/* Iteration Name */}
                <CustomInputField
                    id="iterationName"
                    label="Iteration Name"
                    required
                    value={iterationName || ''}
                    onChange={(id, v) => setIterationName(v)}
                    maxLength={100}
                    placeholder="Enter iteration name"
                />

                {/* Environment + Country */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                        <CustomSelect
                            id="environment"
                            label="Environment"
                            required
                            value={selectedEnvironment}
                            onChange={val => setSelectedEnvironment(val)}
                            options={environmentOptions}
                            placeholder="Select"
                            disabled={mode === 'edit'}
                        />
                    </Box>

                    {shouldShowCountry && (
                        <Box sx={{ flex: 1 }}>
                            <CustomSelect
                                id="country"
                                label="Country"
                                value={selectedCountry}
                                onChange={val => setSelectedCountry(val)}
                                options={countrySelectOptions}
                                placeholder="Select"
                                disabled={mode === 'edit'}
                            />
                        </Box>
                    )}
                </Stack>

                {/* Test Cases Section */}
                <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="h6">Test Cases ({selectedTestCases.length})</Typography>
                        {mode === 'create' && (
                            <ActionButton
                                label="Select Test Cases"
                                intent="primary"
                                size="medium"
                                disabled={noTestCasesInCycle}
                                onClick={() => setShowModal(true)}
                            />
                        )
                        }
                    </Stack>
                    {mode === 'create' && noTestCasesInCycle && (
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                            No test cases are available in this Test Cycle. Please add test cases from
                            the Test Cases tab before starting a new iteration.
                        </Alert>
                    )}
                    {selectedTestCases.length > 0 ? (
                        <>
                            {/* Toolbar: select-all + search + bulk assign */}
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                spacing={1.5}
                                sx={{ mb: 1.5 }}
                            >
                                    <Tooltip title="Select all editable test cases">
                                        <span>
                                            <Checkbox
                                                checked={isAllSelected}
                                                disabled={editableTestCaseIds.length === 0}
                                                onChange={() =>
                                                    setSelectedRows(isAllSelected ? [] : editableTestCaseIds)
                                                }
                                                size="small"
                                            />
                                        </span>
                                    </Tooltip>

                                <Box sx={{ flex: 1 }}>
                                    <SearchBar
                                        value={searchValue}
                                        onChange={e => setSearchValue(e.target.value)}
                                        clearSearch={() => setSearchValue('')}
                                        sx={{ width: '100%' }}
                                    />
                                </Box>

                                    <>
                                        <Box sx={{ minWidth: 180 }}>
                                            <CustomSelect
                                                id="bulkTester"
                                                value={bulkTester}
                                                onChange={val => setBulkTester(val)}
                                                options={userOptions}
                                                placeholder="Assign Tester"
                                            />
                                        </Box>
                                        <ActionButton
                                            label="Apply"
                                            intent="primary"
                                            size="medium"
                                            disabled={!bulkTester || selectedRows.length === 0}
                                            onClick={assignTesterBulk}
                                        />
                                    </>
                            </Stack>

                            <Divider sx={{ mb: 1.5 }} />

                            {/* Test Case List */}
                            <Stack spacing={1}>
                                {loading ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minHeight: 200,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Loading…
                                        </Typography>
                                    </Box>
                                ) : filteredSelected.length ? (
                                    filteredSelected.map((tc, i) => (
                                        <Stack
                                            key={tc.testCaseAssociationId}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                px: 1.5,
                                                py: 1,
                                                bgcolor: 'background.paper',
                                            }}
                                        >
                                            {/* Row checkbox */}
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedRows.includes(tc.testCaseAssociationId)}
                                                    onChange={() => toggleRow(tc.testCaseAssociationId)}
                                                    disabled={tc.isEditable === false}
                                                />

                                            {/* Index + key + summary */}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                <Box component="span" fontWeight={700} sx={{ mr: 0.5 }}>
                                                    #{i + 1}
                                                </Box>
                                                —{' '}
                                                <Box component="span" fontWeight={600} sx={{ mr: 0.5 }}>
                                                    {tc.testCaseKey}:
                                                </Box>
                                                {tc.summary.length > 80
                                                    ? `${tc.summary.slice(0, 80)}…`
                                                    : tc.summary}
                                            </Typography>

                                            {/* Assigned avatar / label */}
                                            <Box
                                                sx={{
                                                    flexShrink: 0,
                                                    width: 90,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {tc.assignedTo ? (() => {
                                                    const raw = showUserDisplayName(tc.assignedTo, users);
                                                    const displayName = typeof raw === 'string' ? raw : '';
                                                    const parts = displayName.trim().split(' ');
                                                    const first = parts[0] || '';
                                                    const last = parts.slice(1).join(' ') || '';
                                                    return (
                                                        <CustomAvatar
                                                            firstName={first}
                                                            lastName={last}
                                                            size={24}
                                                            backgroundColor={tokens.colors.neutral[400]}
                                                            showTitle
                                                        />
                                                    );
                                                })() : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Unassigned
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No test cases match your search.
                                    </Typography>
                                )}
                            </Stack>

                            {/* Non-editable warning */}
                            {hasNonEditableExecutions && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                    <strong>Note:</strong> Some test cases in this iteration are non-editable
                                    because newer executions were created for them in a different iteration
                                    with the same environment and country combination.
                                </Alert>
                            )}
                        </>
                    ) :(
                        <Typography variant="body2" color="text.secondary">
                            {mode === 'edit'
                                ? 'No test cases are currently associated with this iteration.'
                                : noTestCasesInCycle
                                    ? ''
                                    : 'No test cases selected. Click "Select Test Cases" to add.'}
                        </Typography>
                    )}
                </Box>
            </Stack>

            {/* Test Case selection modal */}
            {mode === 'create' && showModal && (
                <TestCaseTable
                    entityId={testCycleId}
                    projectId={projectId}
                    addNotification={addNotification}
                    selectedTestCases={tempSelectedIds}
                    setSelectedTestCases={setTempSelectedIds}
                    show={showModal}
                    handleCloseButton={() => setShowModal(false)}
                    handleAddTestCases={applySelection}
                    setFetchedAssociations={setModalAssociations}
                />
            )}
        </>
    );
};

export default NewIterationForm;